import React from 'react';

import { isSSR } from '@atlaskit/editor-common/is-ssr';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import type {
	AccessibilityUtilsPlugin,
	AccessibilityUtilsPluginState,
} from './accessibilityUtilsPluginType';

const accessibilityUtilsPluginKey = new PluginKey('accessibilityUtilsPlugin');

export const accessibilityUtilsPlugin: AccessibilityUtilsPlugin = ({ api }) => {
	let editorView: EditorView | undefined;
	const setEditorView = (newEditorView: EditorView) => {
		editorView = newEditorView;
	};

	return {
		name: 'accessibilityUtils',
		actions: {
			ariaNotify: (message, ariaLiveElementAttributes) => {
				if (!editorView) {
					// at time of writing, this should never happen
					return;
				}

				const tr = editorView.state.tr;
				tr.setMeta(accessibilityUtilsPluginKey, {
					message,
					ariaLiveElementAttributes,
					key: Date.now().toString(),
				});
				editorView.dispatch(tr);

				return;
			},
		},
		contentComponent: () => {
			if (!editorView || isSSR()) {
				return null;
			}
			return <ContentComponent api={api} />;
		},
		getSharedState(editorState) {
			if (!editorState) {
				return null;
			}
			return accessibilityUtilsPluginKey.getState(editorState);
		},
		pmPlugins() {
			return [
				{
					name: 'get-editor-view',
					plugin: () => {
						return new SafePlugin({
							key: accessibilityUtilsPluginKey,
							state: {
								init: () => ({
									message: '',
									ariaLiveElementAttributes: {},
								}),
								apply: (tr: ReadonlyTransaction, prevState: AccessibilityUtilsPluginState) => {
									const meta = tr.getMeta(accessibilityUtilsPluginKey);
									if (meta) {
										return { ...prevState, ...meta };
									}
									return prevState;
								},
							},
							view(editorView: EditorView) {
								setEditorView(editorView);
								return {};
							},
						});
					},
				},
			];
		},
	};
};

function ContentComponent({
	api,
}: {
	api: ExtractInjectionAPI<AccessibilityUtilsPlugin> | undefined;
}) {
	const { ariaLiveElementAttributes, key, message } = useSharedPluginStateWithSelector(
		api,
		['accessibilityUtils'],
		(states) => {
			return {
				ariaLiveElementAttributes: states.accessibilityUtilsState?.ariaLiveElementAttributes,
				key: states.accessibilityUtilsState?.key,
				message: states.accessibilityUtilsState?.message,
			};
		},
	);
	const role = ariaLiveElementAttributes?.priority === 'important' ? 'alert' : 'status';

	return (
		<VisuallyHidden
			testId={'accessibility-message-wrapper'}
			role={role}
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(role === 'alert' && { key })}
		>
			{message}
		</VisuallyHidden>
	);
}
